import type { ActivityEvent } from "activity-logger";

const AUTH_SENSITIVE_PATHS = new Set(["/callback"]);
const MAX_SIGNED_INT32 = 2_147_483_647;
const RECEIVER_SAFE_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

export const STATIC_ACTIVITY_PATHS = [
  "/",
  "/callback",
  "/run-history",
  "/run-report-card",
  "/demand",
  "/demand_preview",
  "/demand-walk-single",
  "/demand-plan",
  "/supply",
  "/supply-walk",
  "/supply-walk-single",
  "/supply-walk-single-for-hubs",
  "/po-export",
  "/chat",
  "/profile",
  "/forecast-tracking",
  "/three-forecast-tracking",
  "/inventory-health",
  "/inventory-health-with-transfers",
  "/inventory-health-for-hubs",
  "/inventory-wos",
  "/transfer-recommendations-hub-to-spoke",
  "/inventory-health-mrp",
  "/raw-inventory-health",
  "/inventory",
  "/transfer-planning",
  "/transfer-recommendations",
  "/avanti-card-runs",
  "/purchase-order-export-peach-and-lily",
  "/po-inbox",
  "/my-team",
  "/sku-cutovers",
  "/purchasing-calendar",
  "/container-mapping",
  "/help",
  "/help/publish",
  "/manufacturer-stock",
] as const;

const staticActivityPaths = new Set<string>(STATIC_ACTIVITY_PATHS);

export const DYNAMIC_ACTIVITY_ROUTE_TEMPLATES = ["/help/:slug"] as const;

const DYNAMIC_ACTIVITY_PATHS: ReadonlyArray<{
  pattern: RegExp;
  template: string;
}> = [
  {
    pattern: /^\/help\/[^/]+$/,
    template: DYNAMIC_ACTIVITY_ROUTE_TEMPLATES[0],
  },
];

const activityPathname = (rawPath: string): string => {
  try {
    const path = new URL(rawPath, "https://activity.invalid").pathname || "/";
    const rooted = path.startsWith("/") ? path : `/${path}`;
    return rooted.length > 1 ? rooted.replace(/\/+$/, "") : rooted;
  } catch {
    const path = rawPath.split(/[?#]/, 1)[0] || "/";
    const rooted = path.startsWith("/") ? path : `/${path}`;
    return rooted.length > 1 ? rooted.replace(/\/+$/, "") : rooted;
  }
};

export const normalizeActivityPath = (rawPath: string): string => {
  const pathname = activityPathname(rawPath);
  if (staticActivityPaths.has(pathname)) return pathname;
  return (
    DYNAMIC_ACTIVITY_PATHS.find(({ pattern }) => pattern.test(pathname))
      ?.template ?? "/unknown"
  );
};

/**
 * The real page address and its query, the way every other Atomic app logs them: `url`
 * is the pathname plus query string (no scheme, host or fragment), `parameters` the query
 * as an object — the report's filter dimension. Derived from one browser string so
 * nothing else rides along; bounded so a 20-event flush stays inside the keepalive
 * budget. `endpoint` stays the bounded route template beside them.
 */
export const MAX_ACTIVITY_URL_LENGTH = 4096;
export const MAX_ACTIVITY_PARAMETERS = 50;
export const MAX_ACTIVITY_PARAMETER_KEY_LENGTH = 100;
export const MAX_ACTIVITY_PARAMETER_VALUE_LENGTH = 500;

export const activityUrlAndParameters = (
  rawUrl: string | null | undefined,
  rawEndpoint: string,
): { url: string; parameters: Record<string, string> } => {
  const source = typeof rawUrl === "string" && rawUrl.trim() ? rawUrl : rawEndpoint;
  let pathname = "/";
  let search = "";
  try {
    const parsed = new URL(source, "https://activity.invalid");
    pathname = parsed.pathname || "/";
    search = parsed.search;
  } catch {
    const [pathPart, queryPart = ""] = source.split("#")[0].split("?");
    pathname = pathPart.replace(/^[a-z]+:\/\/[^/]+/i, "") || "/";
    search = queryPart ? `?${queryPart}` : "";
  }
  if (!pathname.startsWith("/")) pathname = `/${pathname}`;
  const parameters: Record<string, string> = {};
  for (const [key, value] of new URLSearchParams(search)) {
    if (Object.keys(parameters).length >= MAX_ACTIVITY_PARAMETERS) break;
    if (!key || key.length > MAX_ACTIVITY_PARAMETER_KEY_LENGTH) continue;
    parameters[key] = value.slice(0, MAX_ACTIVITY_PARAMETER_VALUE_LENGTH);
  }
  const url = `${pathname}${search}`.slice(0, MAX_ACTIVITY_URL_LENGTH);
  return { url, parameters };
};

export const isActivityPathExcluded = (rawPath: string): boolean => {
  const path = activityPathname(rawPath);
  return AUTH_SENSITIVE_PATHS.has(path);
};

const receiverSafeOccurredAt = (value: string): boolean => {
  if (!RECEIVER_SAFE_TIMESTAMP.test(value) || Number(value.slice(0, 4)) === 0) {
    return false;
  }
  try {
    return new Date(value).toISOString() === value;
  } catch {
    return false;
  }
};

const receiverSafeRuntime = (value: number | null | undefined): boolean =>
  value === null ||
  value === undefined ||
  (Number.isSafeInteger(value) && value >= 0 && value <= MAX_SIGNED_INT32);

/**
 * The final browser privacy boundary. Only the v1 fields we intentionally send
 * are copied: the templated route, the real path + query (what every other Atomic
 * app logs; the report filters on the parameters), the runtimes and the refresh
 * flag. Fragments, the host and anything else on the event stay behind.
 */
export const sanitizeActivityEvent = (
  event: ActivityEvent,
  expectedCustomerId: string,
): ActivityEvent | null => {
  if (
    event.client !== expectedCustomerId ||
    isActivityPathExcluded(event.endpoint) ||
    (typeof event.url === "string" && isActivityPathExcluded(event.url)) ||
    !receiverSafeOccurredAt(event.occurredAt) ||
    !receiverSafeRuntime(event.frontEndRuntimeMs) ||
    !receiverSafeRuntime(event.backEndRuntimeMs)
  ) {
    return null;
  }
  const endpoint = normalizeActivityPath(event.endpoint);
  const { url, parameters } = activityUrlAndParameters(event.url, event.endpoint);

  return {
    app: "common",
    occurredAt: event.occurredAt,
    endpoint,
    url,
    parameters,
    client: expectedCustomerId,
    frontEndRuntimeMs: event.frontEndRuntimeMs ?? null,
    backEndRuntimeMs: event.backEndRuntimeMs ?? null,
    isAjaxRefresh:
      typeof event.isAjaxRefresh === "boolean" ? event.isAjaxRefresh : false,
  };
};

export const sanitizeActivityEvents = (
  events: ActivityEvent[],
  expectedCustomerId: string,
): ActivityEvent[] =>
  events
    .map((event) => sanitizeActivityEvent(event, expectedCustomerId))
    .filter((event): event is ActivityEvent => event !== null);
